/**
 * Booking attribution + GA4 for the BookingTool (Qatch tracking spec v1, 5 Oct 2026).
 *
 * - Reads brand / site / UTM / click IDs / GA ids from the iframe URL (written by the brand-site
 *   snippet), falls back to a postMessage from the parent page, then to document.referrer (brand only).
 * - Keeps them in memory + sessionStorage (qt_attr) and sends them as `attribution` in create-trip.
 * - Posts funnel steps to booking_tool/track with navigator.sendBeacon (failures ignored).
 * - Loads gtag.js for the company/brand GA4 Measurement ID only after the parent page shares consent
 *   (Consent Mode v2, defaults denied). The purchase itself is sent server-side.
 *
 * Everything is a no-op unless setting_details returns bookingToolDetail.attributionEnabled = true,
 * and every call fails open: tracking must never block or slow a booking.
 */
import { environment } from '../config/environment';

type Dict = { [key: string]: string };

export interface Attribution {
  v: 1;
  brand?: string;
  site?: string;
  utm: { source?: string; medium?: string; campaign?: string; term?: string; content?: string; id?: string };
  clickIds: { gclid?: string; gbraid?: string; wbraid?: string; fbclid?: string; msclkid?: string };
  landingPage?: string;
  referrer?: string;
  firstTouch?: {
    landingPage?: string;
    referrer?: string;
    utm: Attribution['utm'];
    clickIds: Attribution['clickIds'];
    ts?: number;
  };
  ga: { clientId?: string; sessionId?: string };
  sessionId: string;
  consent: { analytics: boolean; ads: boolean };
}

interface Brand {
  slug: string;
  name?: string;
  domains: string[];
  ga4MeasurementId?: string;
}

const STORE_KEY = 'qt_attr';
const SESSION_KEY = 'qt_sid';
const MAX_LEN = 256;
const UTM_KEYS = ['source', 'medium', 'campaign', 'term', 'content', 'id'];
const CLICK_KEYS = ['gclid', 'gbraid', 'wbraid', 'fbclid', 'msclkid'];
const URL_KEYS = ['brand', 'site', 'lp', 'ref', 'ga_cid', 'ga_sid', 'qt',
  ...UTM_KEYS.map(k => 'utm_' + k), ...CLICK_KEYS];
const EMAIL_RE = /[^\s@]+@[^\s@]+\.[^\s@]+/;
const PHONE_RE = /^(\+|00|0)[\d\s().\/-]{8,20}$/;

function safeSession(): Storage | null {
  try { return window.sessionStorage; } catch (e) { return null; }
}

function clean(value: any, isId = false): string | undefined {
  if (value === undefined || value === null) return undefined;
  let v = String(value).trim();
  if (!v) return undefined;
  if (v.length > MAX_LEN) v = v.substring(0, MAX_LEN);
  if (EMAIL_RE.test(v)) return undefined;
  if (!isId) {
    const digits = v.replace(/\D/g, '').length;
    if (PHONE_RE.test(v) && digits >= 9 && digits <= 15) return undefined;
  }
  return v;
}

function cleanUrl(value: any): string | undefined {
  if (!value) return undefined;
  try {
    const u = new URL(String(value));
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return undefined;
    return clean(u.origin + u.pathname);
  } catch (e) {
    return undefined;
  }
}

function hostOf(value: any): string {
  if (!value) return '';
  try {
    const raw = /^https?:\/\//i.test(String(value)) ? String(value) : 'https://' + value;
    return new URL(raw).hostname.toLowerCase().replace(/^www\./, '');
  } catch (e) {
    return '';
  }
}

function randomId(): string {
  try {
    const a = new Uint32Array(2);
    window.crypto.getRandomValues(a);
    return a[0].toString(36) + a[1].toString(36) + Date.now().toString(36);
  } catch (e) {
    return Math.random().toString(36).substring(2) + Date.now().toString(36);
  }
}

class TrackingService {
  private enabled = false;
  private initialised = false;
  private companyId = '';
  private brands: Brand[] = [];
  private ga4Id = '';
  private raw: Dict = {};           // raw values from URL / message (before validation)
  private source: 'url' | 'message' | 'referrer' | 'none' = 'none';
  private consent = { analytics: false, ads: false };
  private consentExplicit = false;  // consent came from the parent page (message / URL), not from the company default
  private gtagLoaded = false;
  private formStartedSent = false;
  private sessionId = '';
  private pendingMessages: MessageEvent[] = [];

  constructor() {
    try {
      this.captureFromUrl();
      window.addEventListener('message', this.onMessage);
    } catch (e) { /* fail open */ }
  }

  /* ---------------------------------------------------------------- */
  /* Capture                                                           */
  /* ---------------------------------------------------------------- */

  private captureFromUrl() {
    const q = new URLSearchParams(window.location.search);
    const fromUrl: Dict = {};
    q.forEach((value, key) => {
      if (URL_KEYS.includes(key) || key.startsWith('ft_')) fromUrl[key] = value;
    });
    // optional consent in the URL (in addition to postMessage)
    if (q.get('consent_analytics')) { this.consent.analytics = q.get('consent_analytics') === '1'; this.consentExplicit = true; }
    if (q.get('consent_ads')) { this.consent.ads = q.get('consent_ads') === '1'; this.consentExplicit = true; }

    const store = safeSession();
    let stored: any = null;
    try { stored = store ? JSON.parse(store.getItem(STORE_KEY) || 'null') : null; } catch (e) { stored = null; }

    if (Object.keys(fromUrl).length) {
      this.raw = fromUrl;
      this.source = 'url';
    } else if (stored && stored.raw) {
      this.raw = stored.raw;
      this.source = stored.source || 'url';
      if (stored.consent && stored.consentExplicit) { this.consent = stored.consent; this.consentExplicit = true; }
    }

    let sid = '';
    try { sid = (store && store.getItem(SESSION_KEY)) || ''; } catch (e) { sid = ''; }
    if (!sid) {
      sid = randomId();
      try { store && store.setItem(SESSION_KEY, sid); } catch (e) { /* ignore */ }
    }
    this.sessionId = sid;
    this.persist();
  }

  private persist() {
    try {
      const store = safeSession();
      store && store.setItem(STORE_KEY, JSON.stringify({ raw: this.raw, source: this.source, consent: this.consent, consentExplicit: this.consentExplicit }));
    } catch (e) { /* ignore */ }
  }

  private allowedOrigin(origin: string): boolean {
    const h = hostOf(origin);
    return !!h && this.brands.some(b => (b.domains || []).map(hostOf).includes(h));
  }

  private onMessage = (event: MessageEvent) => {
    try {
      const data: any = event.data;
      if (!data || typeof data !== 'object') return;
      if (data.type !== 'qatch-attribution' && data.type !== 'qatch-consent') return;
      if (!this.initialised) {
        // brands are not known yet: validate once setting_details has loaded
        if (this.pendingMessages.length < 10) this.pendingMessages.push(event);
        return;
      }
      if (!this.allowedOrigin(event.origin)) return;
      this.applyMessage(data);
    } catch (e) { /* fail open */ }
  };

  private applyMessage(data: any) {
    if (data.type === 'qatch-consent' || data.consent) {
      const c = data.type === 'qatch-consent' ? data : data.consent;
      this.setConsent(c.analytics === true, c.ads === true);
    }
    if (data.type === 'qatch-attribution' && data.params && typeof data.params === 'object') {
      // URL first, then message: only use the message when the URL carried nothing
      if (this.source === 'none' || this.source === 'referrer') {
        const fromMsg: Dict = {};
        Object.keys(data.params).forEach(key => {
          if (URL_KEYS.includes(key) || key.startsWith('ft_')) fromMsg[key] = String(data.params[key]);
        });
        if (Object.keys(fromMsg).length) {
          this.raw = fromMsg;
          this.source = 'message';
          this.persist();
        }
      }
    }
  }

  /* ---------------------------------------------------------------- */
  /* Init (after setting_details)                                      */
  /* ---------------------------------------------------------------- */

  /**
   * Call with the setting_details response. `page` = 'booking' sends booking_tool_open + "opened";
   * 'summary' only prepares GA4 (call summaryPageView once the trip is loaded).
   */
  init(settingResponse: any, page: 'booking' | 'summary' = 'booking') {
    try {
      const data = settingResponse && settingResponse.data;
      const detail = (data && data.bookingToolDetail) || {};
      this.enabled = detail.attributionEnabled === true;
      if (!this.enabled) return;
      this.companyId = data.driverId || '';
      this.brands = Array.isArray(data.brands) ? data.brands : [];
      this.initialised = true;

      // brand-only fallback from document.referrer (parent page hostname)
      if (this.source === 'none' && document.referrer) {
        const brand = this.brandForHost(hostOf(document.referrer));
        if (brand) {
          this.raw = { brand: brand.slug, site: hostOf(document.referrer) };
          this.source = 'referrer';
          this.persist();
        }
      }
      // validate messages that arrived before the brands were known
      const pending = this.pendingMessages;
      this.pendingMessages = [];
      pending.forEach(ev => { if (this.allowedOrigin(ev.origin)) this.applyMessage(ev.data); });

      // Company consent mode:
      //  'parent'  (default) wait until the brand site shares consent (postMessage / URL), Consent Mode v2 denied until then
      //  'granted' the brand sites' own cookie banner covers the booking tool, treat consent as given
      //            (an explicit "denied" from the parent page still wins)
      if (detail.consentMode === 'granted' && !this.consentExplicit) {
        this.consent = { analytics: true, ads: true };
      }

      const brand = this.currentBrand();
      this.ga4Id = (brand && brand.ga4MeasurementId) || detail.ga4MeasurementId || '';
      this.setupGtag();

      if (page === 'booking') {
        this.event('booking_tool_open', {});
        this.step('opened');
      }
    } catch (e) {
      console.error('tracking.init', e);
    }
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  private brandForHost(host: string): Brand | undefined {
    if (!host) return undefined;
    return this.brands.find(b => (b.domains || []).map(hostOf).includes(host));
  }

  private currentBrand(): Brand | undefined {
    const slug = (clean(this.raw.brand) || '').toLowerCase();
    const bySlug = this.brands.find(b => b.slug === slug);
    return bySlug || this.brandForHost(hostOf(this.raw.site));
  }

  /** Validated brand slug: must match a slug from setting_details, else "unknown". */
  brandSlug(): string {
    const b = this.currentBrand();
    return b ? b.slug : 'unknown';
  }

  /* ---------------------------------------------------------------- */
  /* Attribution payload (create-trip)                                 */
  /* ---------------------------------------------------------------- */

  getAttribution(): Attribution | undefined {
    if (!this.enabled) return undefined;
    try {
      const r = this.raw;
      const utm: Attribution['utm'] = {};
      UTM_KEYS.forEach(k => { const v = clean(r['utm_' + k], k === 'id'); if (v) (utm as any)[k] = v; });
      const clickIds: Attribution['clickIds'] = {};
      CLICK_KEYS.forEach(k => { const v = clean(r[k], true); if (v) (clickIds as any)[k] = v; });

      const ftUtm: Attribution['utm'] = {};
      UTM_KEYS.forEach(k => { const v = clean(r['ft_utm_' + k], k === 'id'); if (v) (ftUtm as any)[k] = v; });
      const ftClick: Attribution['clickIds'] = {};
      CLICK_KEYS.forEach(k => { const v = clean(r['ft_' + k], true); if (v) (ftClick as any)[k] = v; });
      const ftLp = cleanUrl(r.ft_lp);
      const hasFirstTouch = !!ftLp || Object.keys(ftUtm).length > 0 || Object.keys(ftClick).length > 0;

      const brand = this.currentBrand();
      return {
        v: 1,
        brand: brand ? brand.slug : undefined,
        site: hostOf(clean(r.site)) || undefined,
        utm,
        clickIds,
        landingPage: cleanUrl(r.lp),
        referrer: cleanUrl(r.ref),
        firstTouch: hasFirstTouch ? { landingPage: ftLp, referrer: cleanUrl(r.ft_ref), utm: ftUtm, clickIds: ftClick } : undefined,
        ga: { clientId: clean(r.ga_cid, true), sessionId: clean(r.ga_sid, true) },
        sessionId: this.sessionId,
        consent: { ...this.consent },
      };
    } catch (e) {
      return undefined;
    }
  }

  /* ---------------------------------------------------------------- */
  /* Funnel steps -> booking_tool/track                                */
  /* ---------------------------------------------------------------- */

  step(step: 'opened' | 'quoted' | 'form_started' | 'submitted') {
    if (!this.enabled || !this.companyId) return;
    try {
      let cuid = '';
      try { cuid = new URLSearchParams(window.location.search).get('cuid') || ''; } catch (e) { cuid = ''; }
      const body = JSON.stringify({ step, companyId: this.companyId, cuid: cuid || undefined, brand: this.brandSlug(), sessionId: this.sessionId });
      const url = environment.API_URL + environment['Booking Tool'].track;
      // text/plain keeps sendBeacon a "simple" cross-origin request (no CORS preflight)
      const sent = navigator.sendBeacon && navigator.sendBeacon(url, new Blob([body], { type: 'text/plain' }));
      if (!sent && typeof fetch === 'function') {
        fetch(url, { method: 'POST', body, keepalive: true, mode: 'no-cors', headers: { 'Content-Type': 'text/plain' } }).catch(() => {});
      }
    } catch (e) { /* ignore */ }
  }

  formStarted() {
    if (this.formStartedSent) return;
    this.formStartedSent = true;
    this.step('form_started');
  }

  /* ---------------------------------------------------------------- */
  /* Consent + GA4                                                     */
  /* ---------------------------------------------------------------- */

  private gtag(...args: any[]) {
    const w = window as any;
    w.dataLayer = w.dataLayer || [];
    if (typeof w.gtag !== 'function') {
      w.gtag = function () { w.dataLayer.push(arguments); };
    }
    w.gtag(...args);
  }

  private setupGtag() {
    if (!this.ga4Id) return;
    // Consent Mode v2 defaults: denied. Nothing is loaded and no cookie is set until consent arrives.
    this.gtag('consent', 'default', {
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      analytics_storage: 'denied',
      wait_for_update: 500,
    });
    if (this.consent.analytics) this.loadGtag();
  }

  setConsent(analytics: boolean, ads: boolean) {
    this.consent = { analytics, ads };
    this.consentExplicit = true;
    this.persist();
    if (!this.enabled || !this.ga4Id) return;
    this.gtag('consent', 'update', {
      analytics_storage: analytics ? 'granted' : 'denied',
      ad_storage: ads ? 'granted' : 'denied',
      ad_user_data: ads ? 'granted' : 'denied',
      ad_personalization: ads ? 'granted' : 'denied',
    });
    if (analytics) this.loadGtag();
  }

  private loadGtag(clientIdOverride?: string) {
    if (this.gtagLoaded || !this.ga4Id) return;
    this.gtagLoaded = true;
    try {
      this.gtag('consent', 'update', {
        analytics_storage: 'granted',
        ad_storage: this.consent.ads ? 'granted' : 'denied',
        ad_user_data: this.consent.ads ? 'granted' : 'denied',
        ad_personalization: this.consent.ads ? 'granted' : 'denied',
      });
      this.gtag('js', new Date());
      const config: any = {
        send_page_view: false,
        cookie_flags: 'SameSite=None;Secure', // the tool runs in a cross-site iframe
        brand: this.brandSlug(),
      };
      const cid = clientIdOverride || clean(this.raw.ga_cid, true);
      if (cid) config.client_id = cid;
      const sid = clean(this.raw.ga_sid, true);
      if (sid && !clientIdOverride) config.session_id = sid;
      this.gtag('config', this.ga4Id, config);

      const src = 'https://www.googletagmanager.com/gtag/js?id=' + encodeURIComponent(this.ga4Id);
      if (!document.head.querySelector(`script[src="${src}"]`)) {
        const s = document.createElement('script');
        s.async = true;
        s.src = src;
        document.head.appendChild(s);
      }
    } catch (e) { /* fail open */ }
  }

  /** GA4 event with the brand parameter on every event. Queued until consent; dropped without it. */
  event(name: string, params: { [k: string]: any }) {
    if (!this.enabled || !this.ga4Id) return;
    try {
      this.gtag('event', name, { ...params, brand: this.brandSlug(), send_to: this.ga4Id });
    } catch (e) { /* ignore */ }
  }

  /**
   * Summary page (/summery, new tab): no parent page and no URL attribution here.
   * Brand + GA client id come from trip-detail; the server only stores a gaClientId when analytics
   * consent was given, so its presence is the consent signal.
   */
  summaryPageView(brandSlug?: string, gaClientId?: string) {
    if (!this.enabled || !this.ga4Id) return;
    try {
      if (brandSlug) this.raw = { ...this.raw, brand: brandSlug };
      if (gaClientId) {
        this.consent = { ...this.consent, analytics: true };
        this.loadGtag(gaClientId);
      }
      this.event('page_view', { page_title: 'Booking summary', page_location: window.location.origin + window.location.pathname });
    } catch (e) { /* ignore */ }
  }
}

export const trackingService = new TrackingService();
